import type { ReactNode } from 'react';
import * as TabsPrimitive from '@radix-ui/react-tabs';
import { cn } from '../lib/cn';

/**
 * Page scaffolding.
 *
 * Four layouts cover the whole application (§9): operational dashboard,
 * master-detail, queue-and-inspector, and configuration workspace. A module
 * picks one; it does not invent a fifth.
 */

export function PageHeader({
  title,
  description,
  breadcrumbs,
  actions,
  meta,
  tabs,
}: {
  title: string;
  description?: ReactNode;
  breadcrumbs?: { label: string; to?: string }[];
  actions?: ReactNode;
  /** Small facts shown under the title: counts, last updated, current period. */
  meta?: ReactNode;
  /** Rendered flush with the bottom border so tabs read as part of the header. */
  tabs?: ReactNode;
}) {
  return (
    <header className="shrink-0 border-b border-line bg-surface">
      <div className="px-6 pt-5">
        {breadcrumbs && breadcrumbs.length > 0 ? (
          <nav aria-label="Breadcrumb" className="mb-1.5">
            <ol className="flex flex-wrap items-center gap-1.5 text-caption text-ink-muted">
              {breadcrumbs.map((crumb, index) => (
                <li key={`${crumb.label}-${index}`} className="flex items-center gap-1.5">
                  {index > 0 ? <span aria-hidden>/</span> : null}
                  <span className={index === breadcrumbs.length - 1 ? 'text-ink-secondary' : ''}>
                    {crumb.label}
                  </span>
                </li>
              ))}
            </ol>
          </nav>
        ) : null}

        <div className="flex flex-wrap items-start justify-between gap-x-6 gap-y-3">
          <div className="min-w-0">
            <h1 className="text-2xl font-semibold text-ink">{title}</h1>
            {description ? (
              <p className="mt-1 max-w-3xl text-base text-ink-muted">{description}</p>
            ) : null}
            {meta ? (
              <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-caption text-ink-muted">
                {meta}
              </div>
            ) : null}
          </div>
          {actions ? <div className="flex shrink-0 items-center gap-2">{actions}</div> : null}
        </div>
      </div>
      {tabs ? <div className="mt-4 px-6">{tabs}</div> : <div className="h-5" />}
    </header>
  );
}

/** The scrolling body of a page. Full width, never a narrow centred column (§7). */
export function PageBody({
  children,
  className,
  padded = true,
}: {
  children: ReactNode;
  className?: string;
  padded?: boolean;
}) {
  return (
    <div className={cn('min-h-0 flex-1 overflow-y-auto', padded && 'px-6 py-5', className)}>
      {children}
    </div>
  );
}

export function Page({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cn('flex h-full min-h-0 flex-col', className)}>{children}</div>;
}

/* -------------------------------------------------------------------------- */
/* Tabs                                                                        */
/* -------------------------------------------------------------------------- */

export interface TabItem {
  value: string;
  label: string;
  count?: number;
  disabled?: boolean;
}

/** Underlined tabs, used in page headers and record workspaces. */
export function Tabs({
  value,
  onValueChange,
  items,
  ariaLabel,
  className,
}: {
  value: string;
  onValueChange: (value: string) => void;
  items: TabItem[];
  ariaLabel: string;
  className?: string;
}) {
  return (
    <TabsPrimitive.Root value={value} onValueChange={onValueChange}>
      <TabsPrimitive.List
        aria-label={ariaLabel}
        className={cn('-mb-px flex items-center gap-1 overflow-x-auto', className)}
      >
        {items.map((item) => (
          <TabsPrimitive.Trigger
            key={item.value}
            value={item.value}
            disabled={item.disabled}
            className={cn(
              'flex shrink-0 items-center gap-1.5 whitespace-nowrap border-b-2 px-3 py-2.5 text-base font-medium transition-colors',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/30',
              'disabled:cursor-not-allowed disabled:opacity-50',
              'data-[state=inactive]:border-transparent data-[state=inactive]:text-ink-muted data-[state=inactive]:hover:border-line-strong data-[state=inactive]:hover:text-ink-secondary',
              'data-[state=active]:border-brand data-[state=active]:text-brand-hover',
            )}
          >
            {item.label}
            {typeof item.count === 'number' ? (
              <span
                className={cn(
                  'rounded-full px-1.5 py-px text-micro font-semibold tabular-nums',
                  value === item.value
                    ? 'bg-brand-soft text-brand-hover'
                    : 'bg-neutral-soft text-ink-muted',
                )}
              >
                {item.count}
              </span>
            ) : null}
          </TabsPrimitive.Trigger>
        ))}
      </TabsPrimitive.List>
    </TabsPrimitive.Root>
  );
}

/** Compact segmented control for switching a view mode. */
export function SegmentedControl<T extends string>({
  value,
  onChange,
  options,
  ariaLabel,
  size = 'md',
}: {
  value: T;
  onChange: (value: T) => void;
  options: { value: T; label: string; icon?: ReactNode }[];
  ariaLabel: string;
  size?: 'sm' | 'md';
}) {
  return (
    <div
      role="radiogroup"
      aria-label={ariaLabel}
      className="inline-flex items-center gap-0.5 rounded-md border border-line bg-surface-muted p-0.5"
    >
      {options.map((option) => {
        const selected = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={selected}
            onClick={() => onChange(option.value)}
            className={cn(
              'inline-flex items-center gap-1.5 whitespace-nowrap rounded font-medium transition-colors',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/30',
              size === 'sm' ? 'h-6 px-2 text-caption' : 'h-7 px-2.5 text-sm',
              selected
                ? 'bg-surface text-ink shadow-xs'
                : 'text-ink-muted hover:text-ink-secondary',
            )}
          >
            {option.icon}
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Master-detail                                                               */
/* -------------------------------------------------------------------------- */

/**
 * A list beside a record.
 *
 * On a tablet in portrait the two panes become separate screens rather than
 * being squeezed side by side (§55) — which is why `hasSelection` exists: the
 * layout needs to know which pane to show when there is only room for one.
 */
export function MasterDetail({
  master,
  detail,
  hasSelection,
  onBack,
  masterWidth = 'md',
}: {
  master: ReactNode;
  detail: ReactNode;
  hasSelection: boolean;
  onBack: () => void;
  masterWidth?: 'sm' | 'md' | 'lg';
}) {
  const widths = {
    sm: 'tl:w-[300px]',
    md: 'tl:w-[380px]',
    lg: 'tl:w-[460px]',
  } as const;

  return (
    <div className="flex h-full min-h-0">
      <aside
        className={cn(
          'flex min-h-0 shrink-0 flex-col border-r border-line bg-surface',
          widths[masterWidth],
          hasSelection ? 'hidden tl:flex' : 'flex w-full tl:w-auto',
        )}
        aria-label="Records"
      >
        {master}
      </aside>
      <section
        className={cn(
          'min-w-0 flex-1 bg-canvas',
          hasSelection ? 'flex flex-col' : 'hidden tl:flex tl:flex-col',
        )}
      >
        {hasSelection ? (
          <div className="shrink-0 border-b border-line bg-surface px-4 py-2 tl:hidden">
            <button
              type="button"
              onClick={onBack}
              className="text-base font-medium text-brand hover:underline"
            >
              ← Back to list
            </button>
          </div>
        ) : null}
        <div className="min-h-0 flex-1 overflow-y-auto">{detail}</div>
      </section>
    </div>
  );
}

/**
 * A work queue beside an inspector.
 *
 * The difference from master-detail is intent: this is a list you are working
 * *through*, so the queue stays narrow, the inspector is wide, and a decision
 * bar sits at the bottom of the inspector.
 */
export function QueueInspector({
  queue,
  inspector,
  hasSelection,
  onBack,
  actions,
}: {
  queue: ReactNode;
  inspector: ReactNode;
  hasSelection: boolean;
  onBack: () => void;
  /** Sticky decision bar. Approving is the point of the screen; it never scrolls away. */
  actions?: ReactNode;
}) {
  return (
    <div className="flex h-full min-h-0">
      <aside
        className={cn(
          'flex min-h-0 shrink-0 flex-col border-r border-line bg-surface',
          'tl:w-[420px]',
          hasSelection ? 'hidden tl:flex' : 'flex w-full tl:w-auto',
        )}
        aria-label="Queue"
      >
        {queue}
      </aside>
      <section
        className={cn(
          'min-w-0 flex-1 bg-canvas',
          hasSelection ? 'flex flex-col' : 'hidden tl:flex tl:flex-col',
        )}
      >
        {hasSelection ? (
          <div className="shrink-0 border-b border-line bg-surface px-4 py-2 tl:hidden">
            <button
              type="button"
              onClick={onBack}
              className="text-base font-medium text-brand hover:underline"
            >
              ← Back to queue
            </button>
          </div>
        ) : null}
        <div className="min-h-0 flex-1 overflow-y-auto">{inspector}</div>
        {actions ? (
          <div className="shrink-0 border-t border-line bg-surface px-5 py-3">{actions}</div>
        ) : null}
      </section>
    </div>
  );
}

/**
 * A configuration workspace: settings navigation beside an editor.
 *
 * Used wherever the spec asks for configuration rather than records — payroll
 * rules, leave types, roles, company settings (§9).
 */
export function ConfigWorkspace({
  sections,
  value,
  onChange,
  children,
  saveBar,
}: {
  /**
   * The sections, as data rather than markup.
   *
   * Structured so this component can lay them out to suit the viewport — down
   * the side where there is room, across the top where there is not. When the
   * nav was an opaque `ReactNode` the only options were to show a 240px column
   * or hide it, and hiding it left Settings and Roles with no way to change
   * section at all below 1024px.
   */
  sections: { value: string; label: ReactNode }[];
  value: string;
  onChange: (value: string) => void;
  children: ReactNode;
  /** Sticky save bar, shown only when something is dirty. */
  saveBar?: ReactNode;
}) {
  return (
    <div className="flex h-full min-h-0 flex-col tl:flex-row">
      {/* Across the top on narrow screens, down the side when there is room. */}
      <nav
        className="shrink-0 overflow-x-auto border-b border-line bg-surface p-2 tl:w-[240px] tl:overflow-y-auto tl:border-b-0 tl:border-r tl:p-3"
        aria-label="Configuration sections"
      >
        <ul className="flex gap-1 tl:flex-col tl:gap-0 tl:space-y-px">
          {sections.map((section) => (
            <li key={section.value} className="shrink-0 tl:shrink">
              <button
                type="button"
                aria-current={section.value === value ? 'true' : undefined}
                onClick={() => onChange(section.value)}
                className={cn(
                  'flex w-full items-center justify-between gap-2 whitespace-nowrap rounded-md',
                  'px-2.5 py-1.5 text-left text-base transition-colors',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/30',
                  section.value === value
                    ? 'bg-brand-soft font-medium text-brand-hover'
                    : 'text-ink-secondary hover:bg-neutral-soft',
                )}
              >
                {section.label}
              </button>
            </li>
          ))}
        </ul>
      </nav>
      <div className="flex min-w-0 flex-1 flex-col">
        <div className="min-h-0 flex-1 overflow-y-auto bg-canvas px-6 py-5">{children}</div>
        {saveBar ? (
          <div className="shrink-0 border-t border-line bg-surface px-6 py-3 shadow-lg">
            {saveBar}
          </div>
        ) : null}
      </div>
    </div>
  );
}
